<template>
  <v-container v-if="!loading" class="pt-6">
    <BorderedContainer 
      label="Time Zone"
    >
      <v-row>
        <v-col cols="2" class="pt-6">
          Preset:
        </v-col>
        <v-col cols="9">
          <v-select
            v-model="selectedTimeZonePreset"
            :items="timeZonePresets" 
            item-value="name"
            item-text="name"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="1" class="pt-6">
          UTC
        </v-col>

        <v-col cols="2">
          <v-select
            v-model="selectedSign"
            :items="signs"
            @change="presetHandler('tz')"
          />
        </v-col>

        <v-col cols="2">
          <v-select
            v-model="selectedTimeZoneHours"
            :items="timeZoneHours"
            @change="presetHandler('tz')"
          />
        </v-col>

        <v-col cols="2" class="pt-6">
          Hours
        </v-col>

        <v-col cols="2">
          <v-select
            v-model="selectedTimeZoneMinutes"
            :items="timeZoneMinutes"
            @change="presetHandler('tz')"
          />
        </v-col>

        <v-col cols="2" class="pt-6">
          Minutes
        </v-col>
      </v-row>
    </BorderedContainer>
    <br>
    <BorderedContainer 
      label="Enable Daylight Savings Time"
      :show-checkbox="true"
      :init-state="dstEnabled"
      @clickEvent="clickEvent"
    >
      <v-row>
        <v-col cols="2" class="pt-6">
          Preset:
        </v-col>
        <v-col cols="9">
          <v-select
            v-model="selectedDstPresets"
            :items="dstPresets"
            item-value="name"
            item-text="name"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="2" class="pt-6">
          DST Offset:
        </v-col>

        <v-col cols="2">
          <v-select
            v-model="selectedDstOffset"
            :items="dstOffsetList"
            @change="presetHandler('dst')"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="2" class="pt-6">
          Start Time:
        </v-col>

        <v-col cols="2">
          <v-select 
            v-model="selectedStartTimeHour"
            :items="startTimeHourList"
            label="hour"
            item-value="value"
            item-text="label"
            @change="presetHandler('dst')"
          />
        </v-col>

        <v-col cols="3">
          <v-select 
            v-model="selectedStartDayOfMonth"
            :items="startDayOfMonthList"
            label="Day of Month"
            @change="presetHandler('dst')"
          />
        </v-col>

        <v-col cols="3">
          <v-select 
            v-model="selectedStartMonth"
            :items="startMonthList"
            label="Month"
            @change="presetHandler('dst')"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="2" class="pt-6">
          End Time:
        </v-col>

        <v-col cols="2">
          <v-select 
            v-model="selectedEndTimeHour"
            :items="endTimeHourList"
            label="hour"
            item-value="value"
            item-text="label"
            @change="presetHandler('dst')"
          />
        </v-col>

        <v-col cols="3">
          <v-select 
            v-model="selectedEndDayOfMonth"
            :items="endDayOfMonthList"
            label="Day of Month"
            @change="presetHandler('dst')"
          />
        </v-col>

        <v-col cols="3">
          <v-select 
            v-model="selectedEndMonth"
            :items="endMonthList"
            label="Month"
            @change="presetHandler('dst')"
          />
        </v-col>
      </v-row>
    </BorderedContainer>
    <v-row>
      <v-spacer/>
      <v-col cols="2">
        <v-btn text @click.native="close">cancel</v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn text :loading="updating" @click.native="updateAssetConfig">submit</v-btn>
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else>
    <v-row>
      <v-col cols="12" class="d-flex justify-center align-center">
        <v-progress-circular
          size="100"
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import { Logger } from "aws-amplify"
const logger = new Logger("Asset Config / Time Settings")

import { getAssetConfig, updateAssetConfig, updateAssetConfigBatch } from '@/services/API.js'

import BorderedContainer from '../components/BorderedContainer.vue'

export default {
  name: 'TimeSettings',

  components: {
    BorderedContainer
  },

  props: {
    assetList: {
    type: Array,
    required: true
   },

   org: {
    type: String,
    required: true
   }
  },

  data() {
    return {
      timeZonePresets: [
        {
          name: 'None'
        },
        {
          name: 'GMT (UTC+0)',
          sign: 'Plus',
          hour: 0,
          minutes: 0
        },
        {
          name: 'Central European Time (UTC + 1)',
          sign: 'Plus',
          hour: 1,
          minutes: 0
        },
        {
          name: 'Eastern European Time (UTC + 2))',
          sign: 'Plus',
          hour: 2,
          minutes: 0
        },
        {
          name: 'US Eastern Standard Time (UTC-5)',
          sign: 'Minus',
          hour: 5,
          minutes: 0
        },
        {
          name: 'US Central Standard Time (UTC-6)',
          sign: 'Minus',
          hour: 6,
          minutes: 0
        },
        {
          name: 'US Mountain Standard Time (UTC-7)',
          sign: 'Minus',
          hour: 7,
          minutes: 0
        },
        {
          name: 'US Pacific Standard Time (UTC-8)',
          sign: 'Minus',
          hour: 8,
          minutes: 0
        },
        {
          name: 'US Alaska Standard Time (UTC-9)',
          sign: 'Minus',
          hour: 9,
          minutes: 0
        },
        {
          name: 'US Hawaii Standard Time (UTC-10)',
          sign: 'Minus',
          hour: 10,
          minutes: 0
        },
        {
          name: 'Australia Western Standard Time (UTC +8)',
          sign: 'Plus',
          hour: 8,
          minutes: 0
        },
        {
          name: 'Australia Central Western Standard Time (UTC+8:45)',
          sign: 'Plus',
          hour: 8,
          minutes: 45
        },
        {
          name: 'Australia Central Standard Time (UTC+9:30)',
          sign: 'Plus',
          hour: 9,
          minutes: 30
        },
        {
          name: 'Australia Eastern Standard Time (UTC+10:00)',
          sign: 'Plus',
          hour: 10,
          minutes: 0
        }
      ],

      selectedTimeZonePreset: 'None',

      signs: [
        'Plus',
        'Minus'
      ],
      selectedSign: '',

      timeZoneHours: [
        0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14
      ],
      selectedTimeZoneHours: '',

      timeZoneMinutes: [
        0, 30, 45
      ],
      selectedTimeZoneMinutes: '',

      dstOffsetList: [
        '1 hour',
        '30 minutes'
      ],
      selectedDstOffset: '',

      startTimeHourList: [
        {
          label: 'Midnight',
          value: 0
        },
        {
          label: '1 AM',
          value: 1
        },
        {
          label: '2 AM',
          value: 2
        },
        {
          label: '3 AM',
          value: 3
        }
      ],
      selectedStartTimeHour: '',

      endTimeHourList: [
        {
          label: 'Midnight',
          value: 0
        },
        {
          label: '1 AM',
          value: 1
        },
        {
          label: '2 AM',
          value: 2
        },
        {
          label: '3 AM',
          value: 3
        }
      ],
      selectedEndTimeHour: '',

      startDayOfMonthList: [
        'First Sunday',
        'Second Sunday',
        'Last Sunday',
        'First Friday',
        'Last Saturday',
        'Friday before last Sunday'
      ],
      selectedStartDayOfMonth: '',

      endDayOfMonthList: [
        'First Sunday',
        'Last Sunday',
        'Last Thursday',
        'First Saturday',
        'Second Sunday'
      ],
      selectedEndDayOfMonth: '',

      startMonthList: [
        'March',
        'April',
        'September',
        'October'
      ],
      selectedStartMonth: '',

      endMonthList: [
        'October',
        'November',
        'April'
      ],
      selectedEndMonth: '',

      dstPresets: [
        {
          name: 'None'
        },
        {
          name: 'European Union / UK',
          offset: '1 hour',
          start: {
            hour: 1,
            day: 'Last Sunday',
            month: 'March'
          },
          end: {
            hour: 1,
            day: 'Last Sunday',
            month: 'October'
          }
        },
        {
          name: 'United States (Except Arizona / Hawaii) / Canada / Australia',
          offset: '1 hour',
          start: {
            hour: 2,
            day: 'Second Sunday',
            month: 'March'
          },
          end: {
            hour: 2,
            day: 'Second Sunday',
            month: 'November'
          }
        },

        {
          name: 'Australia',
          offset: '1 hour',
          start: {
            hour: 2,
            day: 'First Sunday',
            month: 'October'
          },
          end: {
            hour: 3,
            day: 'First Sunday',
            month: 'April'
          }
        }
      ],
      selectedDstPresets: 'None',

      dstEnabled: false,

      loading: false,

      updating: false
      
    }
  },

  computed: {
    lastSelectedAsset() {
      if(!this.assetList) return
      const total = this.assetList.length

      return this.assetList.length > 0 ? this.assetList[total - 1] : null
    },

    payload() {
      const payload = {
        "time_settings": {
          "timezone": {
            "preset": this.selectedTimeZonePreset,
            "sign": this.selectedSign,
            "hour": this.selectedTimeZoneHours,
            "minute": this.selectedTimeZoneMinutes
          },
          "dst": {
            "enabled": this.dstEnabled,
            "preset": this.selectedDstPresets,
            "offset": this.selectedDstOffset,
            "start": {
              "hour": this.selectedStartTimeHour,
              "month": this.selectedStartMonth,
              "day_of_month": this.selectedStartDayOfMonth
            },
            "end": {
              "hour": this.selectedEndTimeHour,
              "month": this.selectedEndMonth,
              "day_of_month": this.selectedEndDayOfMonth
            }
          }
        }
      }
      return payload
    },

    batchPayload() {
      let cloneOfAssets = this.assetList.map(clone => ({...clone})) 

      cloneOfAssets = cloneOfAssets.map(item => ({
        asset_id: item.asset_id,
        ...this.payload
      }))
      
      return cloneOfAssets
    }
  },

  watch: {
    selectedTimeZonePreset() {
      if(this.selectedTimeZonePreset == 'None') return
      const preset = this.timeZonePresets.find(setting => setting.name == this.selectedTimeZonePreset)
      
      this.selectedSign = preset.sign
      this.selectedTimeZoneHours = preset.hour
      this.selectedTimeZoneMinutes = preset.minutes
    },

    selectedDstPresets() {
      if(this.selectedDstPresets == 'None') return
      const preset = this.dstPresets.find(setting => setting.name == this.selectedDstPresets)

      logger.info('dst preset', preset)
      this.selectedEndMonth = preset.end.month
      this.selectedStartMonth = preset.start.month
      this.selectedEndDayOfMonth = preset.end.day
      this.selectedStartDayOfMonth = preset.start.day
      this.selectedEndTimeHour = preset.end.hour
      this.selectedStartTimeHour = preset.start.hour
      this.selectedDstOffset = preset.offset
    }
  },

  mounted() {
    this.getAssetConfig()
  },

  methods: {
    close() {
      this.$emit('close')
    },

    presetHandler(setting) {
      logger.info('presetHandler', setting)
      if(setting == 'tz') {
        this.selectedTimeZonePreset = 'None'
      }
      if(setting == 'dst') {
        this.selectedDstPresets = 'None'
      }
    },

    clickEvent(val) {
      logger.info('clickEvent', val)
      this.dstEnabled = !this.dstEnabled
    },

    async getAssetConfig() {
      this.loading = true
      const assetId = this.lastSelectedAsset ? this.lastSelectedAsset.asset_id : null

      if(assetId) {
        const config = await getAssetConfig(this.org, assetId)
       
        if(config && Object.hasOwn(config, 'device_config') && config.device_config) {
          // timezone settings
          this.selectedSign = config.device_config.time_settings.timezone.sign
          this.selectedTimeZoneHours = config.device_config.time_settings.timezone.hour
          this.selectedTimeZoneMinutes = config.device_config.time_settings.timezone.minute
          this.selectedTimeZonePreset = config.device_config.time_settings.timezone.preset
          // dst settings
          this.dstEnabled = config.device_config.time_settings.dst.enabled
          this.selectedEndMonth = config.device_config.time_settings.dst.end.month
          this.selectedStartMonth = config.device_config.time_settings.dst.start.month
          this.selectedEndDayOfMonth = config.device_config.time_settings.dst.end.day_of_month
          this.selectedStartDayOfMonth = config.device_config.time_settings.dst.start.day_of_month
          this.selectedEndTimeHour = config.device_config.time_settings.dst.end.hour
          this.selectedStartTimeHour = config.device_config.time_settings.dst.start.hour
          this.selectedDstOffset = config.device_config.time_settings.dst.offset
          this.selectedDstPresets = config.device_config.time_settings.dst.preset
        }
      }
      this.loading = false
    },

    async updateAssetConfig() {
      this.updating = true

      const assetId = this.lastSelectedAsset ? this.lastSelectedAsset.asset_id : null

      if(assetId) {
        let updateConfig
        if(this.assetList.length > 1) {
          updateConfig = await updateAssetConfigBatch(this.org, this.batchPayload)
        } 
        else {
          updateConfig = await updateAssetConfig(this.org, assetId, this.payload)
        }
         
        if(updateConfig && Object.hasOwn(updateConfig, 'status')) {
          if(updateConfig.status == 'success') {
            this.updating = false
          }
        }
        else {
          this.updating = false

          throw new Error('Error updating asset config')
        }
    }
  }
}
}

</script>